import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, AlertTriangle, CheckCircle2, XCircle, Clock, Users, TrendingUp, DollarSign, FileWarning } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import api from '../utils/api';

const SectionTitle = ({ icon: Icon, title, subtitle, accent = 'brand-primary' }) => (
  <div className="flex items-start gap-4 mb-6">
    <div className={`w-10 h-10 rounded-xl bg-${accent}/10 border border-${accent}/30 flex items-center justify-center flex-shrink-0`}>
      <Icon className={`w-5 h-5 text-${accent}`} />
    </div>
    <div>
      <h2 className="text-2xl md:text-3xl font-bold text-white">{title}</h2>
      {subtitle && <p className="text-zinc-400 text-sm mt-1">{subtitle}</p>}
    </div>
  </div>
);

const RuleItem = ({ type, text }) => {
  // type: 'allowed' | 'restricted' | 'prohibited' | 'warning' | 'info'
  const config = {
    allowed: { Icon: CheckCircle2, color: 'text-brand-positive', bg: 'bg-brand-positive/5 border-brand-positive/20' },
    restricted: { Icon: AlertTriangle, color: 'text-yellow-400', bg: 'bg-yellow-400/5 border-yellow-400/20' },
    prohibited: { Icon: XCircle, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
    warning: { Icon: FileWarning, color: 'text-red-400', bg: 'bg-red-500/10 border-red-500/30' },
    info: { Icon: ShieldCheck, color: 'text-brand-primary', bg: 'bg-brand-primary/5 border-brand-primary/20' },
  };
  const { Icon, color, bg } = config[type] || config.info;
  // Safely render <b>...</b> tags from the static rule strings (no other markup allowed → prevents XSS).
  const parts = text.split(/(<b>.*?<\/b>)/g).map((seg, i) => {
    const m = seg.match(/^<b>(.*?)<\/b>$/);
    return m ? <strong key={i}>{m[1]}</strong> : <React.Fragment key={i}>{seg}</React.Fragment>;
  });
  return (
    <div className={`flex items-start gap-3 p-4 rounded-lg border ${bg}`}>
      <Icon className={`w-5 h-5 ${color} flex-shrink-0 mt-0.5`} />
      <div className="text-sm text-zinc-200 leading-relaxed">{parts}</div>
    </div>
  );
};

const ChallengeBlock = ({ title, color, phases, rules, payout, icon: Icon }) => (
  <div className={`bg-[#0A0F1C]/70 border border-${color}/20 rounded-2xl p-6 md:p-8`} data-testid={`rules-challenge-${title.toLowerCase().replace(/[^a-z]/g, '-')}`}>
    <div className="flex items-center gap-3 mb-6">
      <div className={`w-12 h-12 rounded-xl bg-${color}/10 border border-${color}/30 flex items-center justify-center`}>
        <Icon className={`w-6 h-6 text-${color}`} />
      </div>
      <h3 className="text-2xl font-bold text-white">{title}</h3>
    </div>

    {/* Phase breakdowns */}
    <div className="space-y-4 mb-6">
      {phases.map((phase, i) => (
        <div key={i} className="bg-[#050505]/60 border border-white/5 rounded-lg p-4">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-3 font-semibold">{phase.name}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {phase.metrics.map((m, j) => (
              <div key={j}>
                <div className="text-[10px] uppercase text-zinc-500 tracking-wider">{m.label}</div>
                <div className={`text-base font-mono font-bold ${m.highlight ? `text-${color}` : 'text-white'}`}>{m.value}</div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>

    {/* Payout */}
    <div className={`bg-gradient-to-r from-${color}/10 to-transparent border border-${color}/20 rounded-lg p-4 mb-4`}>
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className={`w-4 h-4 text-${color}`} />
        <span className="text-xs uppercase tracking-wider text-zinc-400 font-semibold">Payout Rules</span>
      </div>
      <div className="text-sm text-zinc-300">{payout}</div>
    </div>

    {/* Additional rules */}
    {rules && rules.length > 0 && (
      <ul className="space-y-2 text-sm text-zinc-400">
        {rules.map((r, i) => <li key={i} className="flex items-start gap-2"><span className={`text-${color} mt-0.5`}>•</span><span>{r}</span></li>)}
      </ul>
    )}
  </div>
);

const Rules = () => {
  const [version, setVersion] = useState(1);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    api.get('/trading-rules').then(r => { setVersion(r.data.version || 1); setUpdatedAt(r.data.updated_at); }).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-12 px-6 border-b border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-brand-primary/10 border border-brand-primary/30 rounded-full mb-5">
            <ShieldCheck className="w-4 h-4 text-brand-primary" />
            <span className="text-xs uppercase tracking-wider text-brand-primary font-bold">Official · Version {version}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4" data-testid="rules-title">
            Trading Rules<br /><span className="bg-gradient-to-r from-brand-primary to-[#A78BFA] bg-clip-text text-transparent">& Conditions</span>
          </h1>
          <p className="text-base md:text-lg text-zinc-400 max-w-2xl mx-auto">
            Clear, transparent, and legally binding rules for every AYRONN Funding challenge. Read carefully — account violations may result in suspension or termination without payout.
          </p>
          {updatedAt && <p className="text-xs text-zinc-600 mt-4">Last updated: {new Date(updatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>}
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 py-16 space-y-16">

        {/* Common Rules */}
        <section>
          <SectionTitle icon={Users} title="Common Rules" subtitle="These apply to ALL account types — 1-Step, 2-Step, and Instant Funding." />
          <div className="space-y-3">
            <RuleItem type="restricted" text="<b>News Trading Restriction:</b> Trading is allowed, but <b>no opening or closing trades within 30 minutes before AND 30 minutes after high-impact news events</b>. Violations are monitored automatically." />
            <RuleItem type="prohibited" text="<b>High-Frequency Trading (HFT) is strictly prohibited.</b> Any automated scalping, latency-sensitive bots, or tick-level exploitation will result in immediate account termination." />
            <RuleItem type="prohibited" text="<b>Hedging / Reverse Trading across multiple accounts is strictly prohibited.</b> Opening opposite positions on different accounts to exploit drawdown rules = account termination." />
            <RuleItem type="prohibited" text="<b>Arbitrage, latency exploitation, or any unfair technical advantage is strictly prohibited.</b>" />
            <RuleItem type="restricted" text="<b>Account Sharing is NOT allowed.</b> Only the registered account holder may trade. Any evidence of third-party access = termination." />
            <RuleItem type="info" text="<b>Risk Management is mandatory.</b> You must operate within your assigned <b>daily drawdown</b> and <b>maximum drawdown</b> limits at all times." />
            <RuleItem type="warning" text="<b>Rule Violations:</b> Any violation may result in — Account Suspension · Account Breach · Termination <b>without payout</b>. Decisions made by AYRONN are final." />
          </div>
        </section>

        {/* Account-Specific Rules */}
        <section>
          <SectionTitle icon={TrendingUp} title="Account-Specific Rules" subtitle="Each challenge type has its own targets and constraints." accent="[#A78BFA]" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ChallengeBlock
              title="1-Step Challenge"
              color="brand-primary"
              icon={TrendingUp}
              phases={[{
                name: 'Evaluation Phase',
                metrics: [
                  { label: 'Profit Target', value: '9%', highlight: true },
                  { label: 'Max Drawdown', value: '6%' },
                  { label: 'Daily Loss', value: '3%' },
                  { label: 'Min Trading Days', value: '3' },
                ],
              }]}
              payout="80% Profit Split · Daily TRC20 USDT payouts after passing · 1% Floating Rule"
              rules={['Leverage: 1:100 during evaluation, 1:30 once funded', 'Unlimited trading period', 'Weekend trading allowed on crypto only']}
            />
            <ChallengeBlock
              title="2-Step Challenge"
              color="[#A78BFA]"
              icon={ShieldCheck}
              phases={[
                { name: 'Phase 1', metrics: [{ label: 'Profit Target', value: '6%', highlight: true }, { label: 'Max DD', value: '6%' }, { label: 'Daily Loss', value: '3%' }, { label: 'Min Days', value: '3' }] },
                { name: 'Phase 2', metrics: [{ label: 'Profit Target', value: '6%', highlight: true }, { label: 'Max DD', value: '6%' }, { label: 'Daily Loss', value: '3%' }, { label: 'Min Days', value: '3' }] },
              ]}
              payout="80% Profit Split · Daily TRC20 USDT payouts · 30% Consistency Rule · 1% Floating Rule"
              rules={['Must pass both phases to get funded', 'Consistency rule temporarily blocks payouts if a single day > 30% of total profit']}
            />
            <ChallengeBlock
              title="Instant Funding"
              color="[#7B61FF]"
              icon={DollarSign}
              phases={[{
                name: 'Live Funded Account',
                metrics: [
                  { label: 'Profit Target', value: 'None', highlight: true },
                  { label: 'Max Drawdown', value: '7%' },
                  { label: 'Daily Loss', value: '4%' },
                  { label: 'Min Trading Days', value: '0' },
                ],
              }]}
              payout="80% Profit Split · Daily TRC20 USDT payouts from Day 1 · 30% Consistency Rule"
              rules={['No evaluation — get funded instantly after payment', '1% Floating Rule applies', 'Leverage: 1:30']}
            />
          </div>
        </section>

        {/* Payout Rules */}
        <section>
          <SectionTitle icon={DollarSign} title="Payout Rules" subtitle="Our daily-payout policy is our biggest USP — but there are conditions." accent="brand-positive" />
          <div className="space-y-3">
            <RuleItem type="allowed" text="<b>Daily Payouts</b> — Request any day once your account is funded. Paid in USDT via TRC20." />
            <RuleItem type="info" text="<b>Minimum Payout:</b> $10. Processing time: typically within 24 hours of approval." />
            <RuleItem type="restricted" text="<b>30% Consistency Rule (2-Step & Instant):</b> No single trading day may exceed 30% of your total profit. Triggering this freezes payouts temporarily — no account breach." />
            <RuleItem type="restricted" text="<b>1% Floating Rule:</b> Unrealized floating losses exceeding 1% of account balance at any time trigger a warning / payout hold." />
            <RuleItem type="prohibited" text="<b>No payout will be issued if any rule violation is found</b> during the payout review process. AYRONN reserves the right to withhold payouts pending investigation." />
          </div>
        </section>

        {/* Inactivity Rules */}
        <section>
          <SectionTitle icon={Clock} title="Inactivity & Archive Policy" accent="yellow-400" />
          <div className="space-y-3">
            <RuleItem type="info" text="<b>Inactive Status:</b> Accounts with no trades for more than 15 days are marked INACTIVE." />
            <RuleItem type="warning" text="<b>Archived:</b> After 18+ days of inactivity, accounts are moved to ARCHIVED and may require re-activation or re-purchase." />
          </div>
        </section>

        {/* Legal Notice */}
        <section className="pt-4">
          <div className="bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/30 rounded-2xl p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-2">Legal Notice</h2>
                <p className="text-sm text-zinc-300 leading-relaxed">
                  All AYRONN Funding accounts are <b>simulated trading accounts</b> powered by Exness MT5. Trading involves substantial risk — past performance does not guarantee future results. By purchasing a challenge, you acknowledge and agree to these rules. Your acceptance is timestamped and versioned — rules version applicable to your account is the version active at the time of purchase.
                </p>
                <p className="text-xs text-zinc-500 mt-3">
                  Questions? Contact <a href="mailto:ayronnsupport@gmail.com" className="text-brand-primary hover:underline">ayronnsupport@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-6">
          <Link to="/pricing" className="inline-flex items-center gap-2 px-8 py-4 bg-brand-primary text-black font-bold rounded-xl hover:glow-primary transition-all" data-testid="rules-cta-btn">
            I Understand — Get Started
          </Link>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default Rules;
